// Pure helpers behind the App Catalog SKU→WMS mapping (#246, #251, #257).
//
// The generated string on /app-catalog/products/:id/edit is built from the list
// of WMS products mapped to a SKU, joining their codes with "+" in list order
// (1 item -> "A", 2 -> "A+B", none -> ""), each code repeated once per unit of
// its quantity (#251: "A+A+B").
//
// WHERE THAT STRING GOES CHANGED IN #257: it is written into the SKU's
// `sku_code` ("รหัส SKU"), not `sku_name`. sku_code is the field the rest of the
// platform already keys combos by — mdfpf_sku_stock.sku_code, the App stock API
// (GET /api/member/v1/stock?sku_codes=A,B,A+B) and the platform auto-mapper
// (parseSellerSKUCounts / BuildWmsMapFromSellerSKU, Platform.go:340-410) — while
// `sku_name` went back to being free text the operator types.
//
// The exported function NAMES still say "skuName" on purpose: they are the #246
// / #251 public contract that several specs import by name, and renaming them
// would be churn for no behaviour change. Read them as "the generated string".
//
// This module is deliberately free of Vue / DOM / axios imports: ProductEdit.vue
// cannot be mounted under this repo's jest setup, so the join rule lives here
// where it can be unit-tested directly.

// WHICH FIELD IS "THE CODE": man_pn.
//
// The warehouse product row (`wms_product`, Go struct DBWmsProduct) has no
// dedicated `code` column — only `name`, `man_pn` and `barcode`. man_pn is the
// right one because the backend ALREADY IMPLEMENTS THE INVERSE of this feature
// in production: BuildWmsMapFromSellerSKU + parseSellerSKUCounts
// (mdf-bo-api/controllers/Platform.go:340-410) split a seller SKU on "+" and
// resolve every segment with IMDBWmsProduct.FindByManPN, so "A001+A002+A002"
// becomes { A001:1, A002:2 }. "+" as the combo separator over man_pn is a
// locked decision (2026-06-14) and the same convention is used by
// mdfpf_sku_stock.sku_code and Member_stock.go.
//
// So a name joined from man_pn round-trips: the platform auto-mapper can parse
// it back into a wms_map. A name joined from barcode would be a dead string
// that nothing downstream can resolve.
//
// `barcode` then `name` are fallbacks only, so a product with an empty man_pn
// still contributes something recognisable instead of silently vanishing from
// the generated name (it just won't be machine-resolvable).
export function wmsProductCode(product) {
  if (!product || typeof product !== 'object') return '';
  // The V3 list endpoint (GET /api/v3/wms/product) serialises PascalCase
  // (ID / Name / ManPN / BarCode); other BO endpoints use snake_case. Accept both.
  const candidates = [
    product.ManPN,
    product.man_pn,
    product.BarCode,
    product.barcode,
    product.Name,
    product.name,
  ];
  for (const c of candidates) {
    const s = c === null || c === undefined ? '' : String(c).trim();
    if (s !== '') return s;
  }
  return '';
}

// wmsPickupQuantity normalises ONE pickup quantity into a repeat count (#251).
//
// A quantity is a number of physical picks, so the only meaningful values are
// integers >= 1. Everything else — missing, 0, negative, NaN, 'abc', 2.5 —
// becomes 1, which is the pre-#251 behaviour (one pick per mapped product) and
// the safest fallback: a generated name must never lose a code or gain half of
// one. The modal's el-input-number is :min="1" :precision="0" so the UI cannot
// produce those values; a hand-edited stored wms_map can.
//
// Owner ruling 2026-09-12: at most WMS_PICKUP_QUANTITY_MAX units per WMS product
// (the modal's el-input-number carries the same :max), so a stray 99999 can never
// generate a multi-hundred-KB SKU name. Values above the cap are clamped, not
// reset to 1 — the operator meant "many", not "one".
export const WMS_PICKUP_QUANTITY_MAX = 20;

export function wmsPickupQuantity(raw) {
  const n = wmsStoredPickupQuantity(raw);
  if (n > WMS_PICKUP_QUANTITY_MAX) return WMS_PICKUP_QUANTITY_MAX;
  return n;
}

// wmsStoredPickupQuantity is the LOAD-side normaliser: integer >= 1, but NO
// ceiling. A stored value above the cap (hand edit, an older mapper, a backfill)
// is shown as-is in the modal so the operator sees what is really there; the cap
// applies when THEY confirm (wmsPickupQuantity). Clamping on load would let a
// plain "Save" of the product silently rewrite the column while sku_name kept
// the longer code list.
export function wmsStoredPickupQuantity(raw) {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!isFinite(n) || !Number.isInteger(n) || n < 1) return 1;
  return n;
}

// Internal: the code of one entry. An entry is either a bare code (the #246
// signature) or an object. codeOf, when given, wins — that is how
// ProductEdit.vue resolves a {wms_product_id} draft row against the cached WMS
// catalogue without this module importing anything.
function entryCode(entry, codeOf) {
  if (typeof codeOf === 'function') {
    const resolved = codeOf(entry);
    return resolved === null || resolved === undefined ? '' : String(resolved).trim();
  }
  if (entry && typeof entry === 'object') {
    const explicit = entry.code === null || entry.code === undefined ? '' : String(entry.code).trim();
    if (explicit !== '') return explicit;
    return wmsProductCode(entry);
  }
  return entry === null || entry === undefined ? '' : String(entry).trim();
}

// Internal: the quantity of one entry. Bare codes (#246) carry no quantity => 1.
function entryQuantity(entry) {
  if (!entry || typeof entry !== 'object') return 1;
  const raw = [entry.qty, entry.quantity, entry.pickup_quantity, entry.count].find(
    (v) => v !== null && v !== undefined && v !== ''
  );
  return wmsPickupQuantity(raw);
}

// skuNameFromWmsProducts joins WMS product codes (man_pn — see wmsProductCode)
// into a SKU name, repeating each code once per unit of its quantity (#251).
//
//   []                               -> ''
//   ['A']                            -> 'A'
//   ['A', 'B']                       -> 'A+B'
//   ['A', 'B', 'C']                  -> 'A+B+C'
//   [{code:'A', qty:2}]              -> 'A+A'
//   [{code:'A', qty:2}, {code:'B'}]  -> 'A+A+B'
//
// WHY REPEAT instead of writing 'A*2': the backend auto-mapper already does the
// exact inverse in production — parseSellerSKUCounts / BuildWmsMapFromSellerSKU
// (mdf-bo-api/controllers/Platform.go:340-410) split a seller SKU on '+' and
// fold repeated segments into counts, so 'A+A+B' parses straight back into
// { A:2, B:1 }. Any other notation would be a dead string downstream.
//
// Entries may be bare codes (the #246 signature, still supported verbatim) or
// objects carrying a quantity under qty / quantity / pickup_quantity / count.
// A third shape — an object with neither a code nor a quantity, e.g. the stored
// { wms_product_id, pickup_quantity } row — needs the optional codeOf callback
// to resolve its code; without one it contributes nothing.
//
// Order is preserved (it is the order the user built the list in). Blank codes
// are skipped rather than producing a dangling '+' — whatever their quantity —
// and a non-array argument yields '' so a missing mapping can never throw
// mid-save.
export function skuNameFromWmsProducts(codes, codeOf) {
  if (!Array.isArray(codes)) return '';
  const parts = [];
  for (const entry of codes) {
    const code = entryCode(entry, codeOf);
    if (code === '') continue;
    const qty = entryQuantity(entry);
    for (let i = 0; i < qty; i += 1) parts.push(code);
  }
  return parts.join('+');
}

// skuNameFromWmsMap is the name to reach for when the input is a wms_map-shaped
// list ([{ wms_product_id, pickup_quantity }] + a codeOf resolver). Same
// function, clearer at the call site:
//
//   skuNameFromWmsMap(draft, (item) => wmsProductCode(byId(item.wms_product_id)))
export const skuNameFromWmsMap = skuNameFromWmsProducts;

// normalizeWmsMap coerces a SKU's `wms_map` into the canonical array shape
// [{ wms_product_id: Number, pickup_quantity: Number }].
//
// It has to be forgiving: the column is stored as JSON text in the Backoffice,
// so existing Backoffice screens already see it arrive as a JSON string, as ''
// (empty column), as null, or as a real array depending on the endpoint.
// pickup_quantity defaults to 1 (one pick per mapped product) and is never
// allowed below 1 (wmsStoredPickupQuantity — no upper cap on load, see there). Entries without a usable wms_product_id are dropped.
export function normalizeWmsMap(raw) {
  let list = raw;
  if (typeof list === 'string') {
    const trimmed = list.trim();
    if (trimmed === '') return [];
    try {
      list = JSON.parse(trimmed);
    } catch (e) {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const id = Number(item.wms_product_id);
      if (!id || isNaN(id)) return null;
      return {
        wms_product_id: id,
        pickup_quantity: wmsStoredPickupQuantity(item.pickup_quantity),
      };
    })
    .filter(Boolean);
}

export default skuNameFromWmsProducts;

// --------------------------------------------------------------------------
// #257 — the generated "A+A+B" string moved from sku_name to sku_code.
// --------------------------------------------------------------------------

// SKU_CODE_MAX_LENGTH is the WIDTH OF THE COLUMN, not a UI preference:
// `mdfpf_product_sku.sku_code varchar(100) NOT NULL DEFAULT ''`. 20 units of a
// ~10-char man_pn still fit, but nothing stops an operator from mapping several
// products at 20 units each, and a code that reaches the BE over-long is either
// truncated (silently breaking the stock key `mdfpf_sku_stock.sku_code` and the
// platform auto-mapper's round-trip) or rejected mid-save. The modal refuses to
// confirm instead, while the length is on screen the whole time.
export const SKU_CODE_MAX_LENGTH = 100;

// skuCodeLength is the character count of a code, tolerating null/undefined/a
// number (the preview renders it on every keystroke, so it must never throw).
export function skuCodeLength(code) {
  if (code === null || code === undefined) return 0;
  return String(code).length;
}

// skuCodeWithinLimit: is this code short enough to store? INCLUSIVE at the
// boundary (exactly 100 chars is a legal varchar(100) value). An empty code is
// within the limit — clearing the mapping is allowed, `sku_code` defaults to ''.
export function skuCodeWithinLimit(code, max) {
  const ceiling = max === null || max === undefined ? SKU_CODE_MAX_LENGTH : Number(max);
  if (!isFinite(ceiling)) return true;
  return skuCodeLength(code) <= ceiling;
}

// skuHasFlashSale answers "does this SKU row already use the flash-sale fields?"
// — the rule behind the #257 checkbox's initial state.
//
// It accepts BOTH shapes a row can be in: the form row the page edits
// (`flash_price` + `flashRange` as the date-picker's [start, end]) and the raw
// BE row (`flash_starts_at` / `flash_ends_at`). A flash price of 0, '' or null
// is NOT a flash sale — 0 is the column default and what clearing the input
// leaves behind, so treating it as "has flash" would pop the columns open on
// every product.
export function skuHasFlashSale(sku) {
  if (!sku || typeof sku !== 'object') return false;
  const price = sku.flash_price;
  if (price !== null && price !== undefined && price !== '' && Number(price) > 0) return true;
  if (Array.isArray(sku.flashRange) && sku.flashRange.some((v) => v !== null && v !== undefined && v !== '')) {
    return true;
  }
  if (sku.flash_starts_at) return true;
  if (sku.flash_ends_at) return true;
  return false;
}

// anySkuHasFlashSale: the OR over a product's SKU list. A new product (no rows,
// or a non-array) has no flash sale, so the columns start hidden.
export function anySkuHasFlashSale(skus) {
  if (!Array.isArray(skus)) return false;
  return skus.some((s) => skuHasFlashSale(s));
}
